import type { ResolutionContext } from '@src/components/report-generator/report-reusables/relationshipResolver';
import type {
  FieldOption,
  FieldState,
  FieldValue,
  Panel,
  ReportTemplate,
  Theme,
} from '@src/components/report-generator/report-reusables/report-typing';

import type { ReportTemplateId } from '@src/components/report-generator/report-reusables/reportTemplates';

import {
  applyFieldChange,
  buildDependencyMap,

  resolveAutomaticFields,
} from '@src/components/report-generator/report-reusables/relationshipResolver';

import {
  collectFields,
  extractFields,
  getAllSectionsAsArray,
  SizeToDims,
} from '@src/components/report-generator/report-reusables/report-typing';

import {
  getAllTemplateEntries,
  getDefaultTemplateId,
  getDropdownOptionsForKey,
  getTemplateById,
} from '@src/components/report-generator/report-reusables/reportTemplates';

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

interface SerializedReportData {
  version?: string;
  templateId?: ReportTemplateId;
  pages?: ReportTemplateId[];
  values?: Record<string, Partial<FieldState>>;
}

export const useUpdatedReportStore = defineStore('updated-report-store', () => {
  // ─── State ──────────────────────────────────────────────────────────────────
  // Single shared values map — field keys are globally unique. When multiple
  // pages share the same key (e.g. patientName, scanDate) the value is shared.
  const values = ref<Record<string, FieldState>>({});
  const dependencyMap = ref<Map<string, FieldState[]>>();
  const resolutionContext = ref<ResolutionContext>();
  const isEditing = ref(true);

  // Ordered list of loaded template IDs (pages). Index 0 is the default page.
  const loadedPages = ref<ReportTemplateId[]>([]);

  // Cache of resolved template objects keyed by template ID
  const templateCache = ref<Record<string, ReportTemplate>>({});

  // ─── Computed ───────────────────────────────────────────────────────────────

  /** The list of all page entries with id + label, for UI rendering */
  const pages = computed(() =>
    loadedPages.value.map((tid, index) => {
      const template = templateCache.value[tid];
      return {
        templateId: tid,
        label: template?.label ?? tid,
        index,
      };
    }),
  );

  // ─── Internal Helpers ───────────────────────────────────────────────────────

  /**
   * Merge a template's fields into the shared values map.
   * Only adds keys that don't already exist (preserving shared values).
   */
  function mergeTemplateFields(template: ReportTemplate) {
    const allSections = getAllSectionsAsArray(template);
    const templateDefaults = extractFields(allSections);

    Object.keys(templateDefaults).forEach((key) => {
      if (!(key in values.value)) {
        values.value[key] = templateDefaults[key];
      }
    });
  }

  /** Rebuild dependency map and resolution context from current values */
  function rebuildDependencies() {
    dependencyMap.value = buildDependencyMap(values.value);
    resolutionContext.value = {
      store: values.value,
    };
  }

  function loadCustomOptions(key: string) {
    const fieldState = values.value[key];
    if (!fieldState || !fieldState.canAddAdditional) return;

    // Load from localStorage
    const saved = localStorage.getItem(`custom_options_${key}`);
    let customOpts: FieldOption[] = [];
    if (saved) {
      try {
        customOpts = JSON.parse(saved) as FieldOption[];
      }
      catch (e) {
        console.error('Failed to parse custom options for key:', key, e);
      }
    }

    // Sync from neighbors' localStorage if available
    if (fieldState.neighborKeys) {
      fieldState.neighborKeys.forEach((nKey) => {
        const nSaved = localStorage.getItem(`custom_options_${nKey}`);
        if (nSaved) {
          try {
            const nOpts = JSON.parse(nSaved) as FieldOption[];
            nOpts.forEach((opt) => {
              if (!customOpts.some(o => o.id === opt.id)) {
                customOpts.push(opt);
              }
            });
          }
          catch (e) {
            console.error(
              'Failed to parse custom options for neighbor:',
              nKey,
              e,
            );
          }
        }
      });
    }

    if (customOpts.length > 0) {
      if (!fieldState.options) {
        fieldState.options = [];
      }
      customOpts.forEach((opt) => {
        if (!fieldState.options!.some(o => o.id === opt.id)) {
          fieldState.options!.push(opt);
        }
      });
    }
  }

  function persistCustomOptions(key: string) {
    const fieldState = values.value[key];
    if (!fieldState || !fieldState.canAddAdditional) return;

    // Collect the original default options from ALL loaded templates
    const defaultOpts: FieldOption[] = [];

    for (const tid of loadedPages.value) {
      const template = templateCache.value[tid];
      if (template) {
        const field = collectFields(getAllSectionsAsArray(template)).find(
          f => f.key === key,
        );
        if (field?.options) {
          field.options.forEach((opt) => {
            if (!defaultOpts.some(d => d.id === opt.id)) {
              defaultOpts.push(opt);
            }
          });
        }
      }
    }

    const currentOpts = fieldState.options || [];
    const customOpts = currentOpts.filter(
      opt => !defaultOpts.some(d => d.id === opt.id),
    );

    localStorage.setItem(`custom_options_${key}`, JSON.stringify(customOpts));

    // Propagate custom options to neighbors in localStorage
    if (fieldState.neighborKeys) {
      fieldState.neighborKeys.forEach((nKey) => {
        localStorage.setItem(
          `custom_options_${nKey}`,
          JSON.stringify(customOpts),
        );
      });
    }
  }

  /**
   * Load a template by ID, cache it, merge its fields, and load custom options.
   */
  function loadTemplate(templateId: ReportTemplateId) {
    const template = getTemplateById(templateId);
    if (!template) {
      console.error('Failed to load template for ID:', templateId);
      return;
    }

    templateCache.value[templateId] = template;
    mergeTemplateFields(template);

    // Load custom options for dropdown fields that allow additions
    Object.keys(values.value).forEach((key) => {
      const fieldState = values.value[key];
      if (fieldState.type === 'dropdown' && fieldState.canAddAdditional) {
        loadCustomOptions(key);
      }
    });

    rebuildDependencies();
    void resolveAutomaticFields(resolutionContext.value!);
  }

  // ─── Page Management ────────────────────────────────────────────────────────

  function ensureDefaultPageLoaded() {
    if (loadedPages.value.length === 0) {
      const defaultId = getDefaultTemplateId();
      loadedPages.value = [defaultId];
      loadTemplate(defaultId);
    }
  }

  /**
   * Add a report page with the given template ID.
   * No-op if a page with this template is already loaded.
   */
  function addPage(templateId: ReportTemplateId) {
    if (loadedPages.value.includes(templateId)) {
      console.warn(`Page with template "${templateId}" is already loaded.`);
      return;
    }

    loadedPages.value.push(templateId);
    loadTemplate(templateId);
  }

  /**
   * Remove a report page by template ID.
   * Any page can be removed — including the default.
   * Cannot remove if it's the only page.
   */
  function removePage(templateId: ReportTemplateId) {
    if (loadedPages.value.length <= 1) {
      console.warn('Cannot remove the last remaining page.');
      return;
    }

    const idx = loadedPages.value.indexOf(templateId);
    if (idx === -1) return;

    loadedPages.value.splice(idx, 1);
    delete templateCache.value[templateId];

    // We do NOT remove values for keys unique to this template —
    // they stay in case the user re-adds the page later in the session.
  }

  /**
   * Check whether a page with the given template ID is loaded.
   */
  function hasPage(templateId: ReportTemplateId): boolean {
    return loadedPages.value.includes(templateId);
  }

  /**
   * Get the pages array (template IDs in order).
   */
  function getPages(): ReportTemplateId[] {
    ensureDefaultPageLoaded();
    return loadedPages.value;
  }

  // ─── Template/Panel Accessors (per-page) ────────────────────────────────────

  function getTemplateForPage(
    templateId: ReportTemplateId,
  ): ReportTemplate | null {
    ensureDefaultPageLoaded();
    return templateCache.value[templateId] || null;
  }

  function getPanelsForPage(templateId: ReportTemplateId): Panel[] {
    const template = getTemplateForPage(templateId);
    return template?.layout.panels || [];
  }

  function getThemeForPage(templateId: ReportTemplateId): Theme {
    const template = getTemplateForPage(templateId);
    if (!template) {
      throw new Error(`No template loaded for ID: ${templateId}`);
    }
    return template.theme;
  }

  function getTemplateDimsForPage(templateId: ReportTemplateId): {
    width: number;
    height: number;
    unit: string;
  } {
    const template = getTemplateForPage(templateId);
    if (template) {
      const { size } = template.layout;
      return SizeToDims[size] || { width: 0, height: 0, unit: 'px' };
    }
    return { width: 0, height: 0, unit: 'px' };
  }

  // ─── Backward-Compatible Accessors ──────────────────────────────────────────
  // These operate on the first loaded page (default) for code that hasn't
  // been updated to multi-page awareness yet.

  function getTemplate(): ReportTemplate | null {
    ensureDefaultPageLoaded();
    const firstId = loadedPages.value[0];
    return firstId ? templateCache.value[firstId] || null : null;
  }

  function getPanels(): Panel[] {
    const template = getTemplate();
    return template?.layout.panels || [];
  }

  function getTheme(): Theme {
    ensureDefaultPageLoaded();
    const template = getTemplate();
    if (!template) {
      throw new Error('No template loaded');
    }
    return template.theme;
  }

  function getTemplateDims(): {
    width: number;
    height: number;
    unit: string;
  } {
    ensureDefaultPageLoaded();
    const template = getTemplate();
    if (template) {
      const { size } = template.layout;
      return SizeToDims[size] || { width: 0, height: 0, unit: 'px' };
    }
    return { width: 0, height: 0, unit: 'px' };
  }

  function getLoadedTemplateId(): ReportTemplateId {
    ensureDefaultPageLoaded();
    return loadedPages.value[0];
  }

  // ─── Dropdown Options ───────────────────────────────────────────────────────

  function addDropdownOption(key: string, option: FieldOption) {
    ensureDefaultPageLoaded();
    const fieldState = values.value[key];
    if (!fieldState) return;

    if (!fieldState.options) {
      fieldState.options = [];
    }

    if (!fieldState.options.some(o => o.id === option.id)) {
      fieldState.options.push(option);
    }

    // Sync option to neighbor dropdowns in memory
    if (fieldState.neighborKeys) {
      fieldState.neighborKeys.forEach((nKey) => {
        const neighbor = values.value[nKey];
        if (neighbor) {
          if (!neighbor.options) {
            neighbor.options = [];
          }
          if (!neighbor.options.some(o => o.id === option.id)) {
            neighbor.options.push(option);
          }
        }
      });
    }

    // Persist to local storage
    persistCustomOptions(key);
  }

  // ─── Field Operations (shared values map — no pageId needed) ────────────────

  function setValue(key: string, value: FieldValue) {
    ensureDefaultPageLoaded();
    if (!values.value[key]) {
      values.value[key] = {
        key,
        value,
        type: 'text',
      };
    }
    else {
      values.value[key].value = value;
    }

    void applyFieldChange(
      key,
      value,
      dependencyMap.value!,
      resolutionContext.value!,
    );

    // If the changed value field has options, the value should be present in options
    // and if not it should be created.
    if (values.value[key].options && value !== '' && value !== null) {
      const options = values.value[key].options;
      if (typeof value === 'string' && !options.some(o => o.id === value)) {
        const newOption: FieldOption = { id: value, label: value };
        if (values.value[key].canAddAdditional) {
          addDropdownOption(key, newOption);
        }
        else {
          options.push(newOption);
        }
      }
    }
  }

  function getValue(key: string): FieldValue | undefined {
    return values.value[key]?.value;
  }

  function getFieldState(key: string): FieldState | undefined {
    return values.value[key];
  }

  function getAllValues(): Record<string, FieldState> {
    // Return a copy of the object:
    const result: Record<string, FieldState> = {};
    Object.keys(values.value).forEach((key) => {
      result[key] = { ...values.value[key] };
    });

    return result;
  }

  // ─── Edit Lock ──────────────────────────────────────────────────────────────

  function lockAllFields() {
    isEditing.value = false;
    Object.keys(values.value).forEach((key) => {
      values.value[key].isDisabled = true;
    });
  }

  function unlockAllFields() {
    isEditing.value = true;
    Object.keys(values.value).forEach((key) => {
      // If a field is non editable, we should keep it locked
      if (values.value[key].isEditable === false) {
        values.value[key].isDisabled = true;
        return;
      }
      values.value[key].isDisabled = false;
    });
  }

  // ─── Resolution ─────────────────────────────────────────────────────────────

  function resolveAutomaticFieldsInContext() {
    ensureDefaultPageLoaded();
    void resolveAutomaticFields(resolutionContext.value!);
  }

  // ─── Legacy setLoadedTemplateId (backward compat) ───────────────────────────
  // Some external code may call this directly. We adapt it to the new model:
  // it resets the pages array to a single page with the given template.

  function setLoadedTemplateId(templateId: ReportTemplateId) {
    // Clear existing state
    values.value = {};
    templateCache.value = {};
    loadedPages.value = [templateId];
    loadTemplate(templateId);
  }

  // ─── Serialization ─────────────────────────────────────────────────────────

  function serialize(): string {
    ensureDefaultPageLoaded();
    return JSON.stringify(
      {
        version: '2.0',
        pages: loadedPages.value,
        values: values.value,
      },
      null,
      2,
    );
  }

  function deserialize(jsonString: string): boolean {
    try {
      const data = JSON.parse(jsonString) as SerializedReportData;

      // ── v1.0 backward compatibility ──
      // TODO: Remove this block once v1.0 support is no longer needed
      if (data.version === '1.0' || (data.templateId && !data.pages)) {
        const templateId = data.templateId || getDefaultTemplateId();
        setLoadedTemplateId(templateId);

        // Merge saved values over template defaults
        const mergedValues: Record<string, FieldState> = { ...values.value };
        if (data.values) {
          const valuesObj = data.values;
          Object.keys(valuesObj).forEach((key) => {
            const val = valuesObj[key];
            if (!val) return;
            if (mergedValues[key]) {
              mergedValues[key] = {
                ...mergedValues[key],
                value: val.value !== undefined ? val.value : mergedValues[key].value,
                options: val.options || mergedValues[key].options,
                isDisabled:
                  val.isDisabled !== undefined
                    ? val.isDisabled
                    : mergedValues[key].isDisabled,
              };
            }
            else if (val.value !== undefined) {
              mergedValues[key] = val as FieldState;
            }
          });
        }

        values.value = mergedValues;
        rebuildDependencies();
        unlockAllFields();
        return true;
      }

      // ── v2.0 multi-page format ──
      if (!data.pages || !data.values) {
        throw new Error('Invalid .a19 file format');
      }

      // Reset and load each page
      values.value = {};
      templateCache.value = {};
      loadedPages.value = [];

      for (const tid of data.pages) {
        loadedPages.value.push(tid);
        loadTemplate(tid);
      }

      // Merge saved values over template defaults
      const mergedValues: Record<string, FieldState> = { ...values.value };
      const valuesObj = data.values;
      Object.keys(valuesObj).forEach((key) => {
        const val = valuesObj[key];
        if (!val) return;
        if (mergedValues[key]) {
          mergedValues[key] = {
            ...mergedValues[key],
            value: val.value !== undefined ? val.value : mergedValues[key].value,
            options: val.options || mergedValues[key].options,
            isDisabled:
              val.isDisabled !== undefined
                ? val.isDisabled
                : mergedValues[key].isDisabled,
          };
        }
        else if (val.value !== undefined) {
          mergedValues[key] = val as FieldState;
        }
      });

      values.value = mergedValues;
      rebuildDependencies();
      unlockAllFields();

      return true;
    }
    catch (e) {
      console.error('Failed to deserialize .a19 file:', e);
      throw e;
    }
  }

  // ─── Reset ──────────────────────────────────────────────────────────────────

  function resetStore() {
    values.value = {};
    dependencyMap.value = undefined;
    resolutionContext.value = undefined;
    isEditing.value = true;
    loadedPages.value = [];
    templateCache.value = {};
    ensureDefaultPageLoaded();
  }

  return {
    // state
    values,
    isEditing,
    loadedPages,
    pages,
    // page management
    addPage,
    removePage,
    hasPage,
    getPages,
    // per-page accessors
    getTemplateForPage,
    getPanelsForPage,
    getThemeForPage,
    getTemplateDimsForPage,
    // backward-compatible accessors (first page)
    getTemplate,
    getPanels,
    getTheme,
    getTemplateDims,
    getLoadedTemplateId,
    // field operations (shared values map)
    setValue,
    getValue,
    getFieldState,
    getAllValues,
    addDropdownOption,
    // edit lock
    lockAllFields,
    unlockAllFields,
    // resolution
    resolveAutomaticFieldsInContext,
    // legacy
    setLoadedTemplateId,
    getDropdownOptionsForKey,
    getAllTemplateEntries,
    // serialization
    serialize,
    deserialize,
    resetStore,
  };
});
